<template>
	<div>
		<slot name="label">
			<div v-if="label" class="tw-pb-1.5 tw-font-medium tw-text-gray-600">
				<span>{{ label }}</span>
				<span v-if="props.rules.includes('required')" class="tw-text-error-400">*</span>
			</div>
		</slot>
		<div
			class="input-container tw-flex tw-flex-1 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-bg-white tw-px-3 tw-py-3.5 tw-transition-all"
			:class="computedClasses"
		>
			<textarea
				class="tw-flex tw-w-full tw-flex-1 tw-text-base !tw-placeholder-gray-500 tw-outline-none disabled:tw-cursor-not-allowed"
				v-model="inputModel"
				:placeholder="placeholder"
				:disabled="disabled"
				:rows="rows"
				:cols="cols"
				v-bind="$attrs"
			>
			</textarea>
		</div>
		<div
			:class="rules.length || hint || allocateBottomSpace ? 'tw-pt-1 tw-text-sm' : ''"
			:style="rules.length || hint || allocateBottomSpace ? 'min-height: 20px' : ''"
		>
			<div v-if="!errorMessage" class="tw-text-gray-600">
				{{ hint }}
			</div>
			<div v-if="errorMessage" class="tw-text-error-500">
				{{ errorMessage }}
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import { debounce as debounceFunc } from 'lodash';
import { useFieldValidation } from '@/v2/utils/forms';

const props = defineProps({
	modelValue: {},
	label: {
		type: String,
		default: '',
	},
	placeholder: {
		type: String,
		default: '',
	},
	hint: {
		type: String,
		default: '',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	rules: {
		type: Array,
		default: () => [],
	},
	debounce: {
		type: Number,
		default: 0,
	},
	rows: {
		type: Number,
		default: 5,
	},
	cols: {
		type: Number,
		default: 50,
	},
	allocateBottomSpace: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['update:modelValue']);

const emitModel = (val) => {
	emit('update:modelValue', val);
};

const inputModel = computed({
	get() {
		return props.modelValue;
	},
	set: props.debounce ? debounceFunc(emitModel, props.debounce) : emitModel,
});

const { errorMessage, hasErrorMessage } = useFieldValidation(
	() => props.modelValue,
	() => props.rules,
);

const computedClasses = computed(() => {
	const res = [];
	if (hasErrorMessage.value) {
		res.push(
			'tw-border-error-300 focus-within:tw-border-error-300 focus-within:tw-ring focus-within:tw-ring-error-500/[.24]',
		);
	} else {
		res.push('tw-border-gray-300');
	}
	if (!hasErrorMessage.value && !props.disabled) {
		res.push('focus-within:tw-border-brand-300');
	}
	if (props.disabled) {
		res.push('tw-text-gray-500');
	}
	return res;
});
</script>

<style lang="scss" scoped></style>
